import { Clapperboard } from 'lucide-react';
import type { NavigationItem, NavigationCategory } from '@/shared/types/navigation';
import { storiesNavItems } from '@/contexts/stories';
import { charactersNavItems } from '@/contexts/characters';
import { mediaNavItems } from '@/contexts/media';

// Public gallery — a single nav entry so the sidebar AND the dashboard tiles
// (both read getNavigationItems) stay in sync. (Reachable without auth too, but
// in-app it's just another tool.)
const galleryNavItem: NavigationItem = {
  id: 'gallery',
  category: 'tool',
  name: 'gallery.name',
  description: 'gallery.description',
  icon: Clapperboard,
  href: '/gallery',
};
// audio-stories + books are no longer top-level nav entries. A story's
// narrations and illustrated books live inside the story itself (see
// StoryWorkspace); their standalone list routes now redirect to /stories.
// habits + challenges moved to the standalone @owlcat/habits app (src/habits).
// Family management moved to the central accounts profile (accounts.owlcat) —
// the portal "Family" nav entry was removed; account/people control lives in
// the profile. Their dedicated portal routes + nav entries were removed.

// Lazy-initialize to avoid circular dependency issues during module loading.
// Barrel imports may not have finished initializing when this module first loads.
let _allItems: NavigationItem[] | null = null;

function getAllItems(): NavigationItem[] {
  if (!_allItems) {
    _allItems = [
      ...storiesNavItems,
      galleryNavItem,
      ...charactersNavItems,
      ...mediaNavItems,
    ];
  }
  return _allItems;
}

export const getNavigationItems = (): NavigationItem[] => getAllItems();

export const getByCategory = (category: NavigationCategory): NavigationItem[] =>
  getAllItems().filter(item => item.category === category);

export const getTools = (): NavigationItem[] => getByCategory('tool');

export const getFamily = (): NavigationItem[] => getByCategory('family');

export const getById = (id: string): NavigationItem | undefined =>
  getAllItems().find(item => item.id === id);
